import type { ElementOrComponent } from '@findigs/helpers/src/helpers/polymorphic';
import { useShouldUseFrameworkLink } from '~/hooks/use-should-use-framework-link';

export type LogoLinkProps<E extends ElementOrComponent> = Omit<
  React.HTMLAttributes<HTMLAnchorElement>,
  'href'
> & {
  /**
   * The framework's built-in `Link` component to be used.
   *
   * If no frameworks are in use, use `a` (anchor tag).
   *
   * @example GatsbyLink, RemixLink, etc
   */
  frameworkLink: E;
  /**
   * The name of the prop that should be passed into the component
   * referenced in `frameworkLink` to specify the destination url.
   *
   * If no frameworks are in use, use `href`.
   * @example For GatsbyLink and RemixLink the prop would be 'to'
   */
  frameworkLinkProp: keyof React.ComponentPropsWithoutRef<E>;

  href?: string;
};

export function LogoLink<E extends ElementOrComponent>({
  href,
  frameworkLink: Link,
  frameworkLinkProp,
  children,
  ...props
}: LogoLinkProps<E>) {
  const shouldUseFrameworkLink = useShouldUseFrameworkLink();

  if (href == null) return <>{children}</>;

  if (shouldUseFrameworkLink(href)) {
    return (
      <Link {...props} {...({ [frameworkLinkProp]: href } as any)}>
        {children}
      </Link>
    );
  }

  return (
    <a {...props} href={href}>
      {children}
    </a>
  );
}
